import type { ViewGuide } from "@/guides/types";
import { cn } from "@u/cn";
import { type ReactNode } from "react";
import { DashboardGuide } from "./DashboardGuide";
import { DashboardHeader, type DashboardBreadcrumb } from "./DashboardHeader";

export interface DashboardPageProps {
  title: string;
  description?: string;
  breadcrumbs?: DashboardBreadcrumb[];
  primaryAction?: ReactNode;
  secondaryActions?: ReactNode;
  /** Rendered inline right after the title text (e.g. a count badge). */
  titleBadge?: ReactNode;
  /** Per-view user guide — renders a help button + tour in the header. */
  guide?: ViewGuide;
  /** Custom content for the header's title-aside slot. Overrides the
   *  guide-derived one (use when the guide lives elsewhere, e.g. a subheader). */
  titleAside?: ReactNode;
  rail?: ReactNode;
  /** Drop the header entirely — for dense operational boards (KDS) where the
   *  sidebar already carries the context and vertical space is precious. */
  hideHeader?: boolean;
  className?: string;
  children: ReactNode;
}

export function DashboardPage({
  title,
  description,
  breadcrumbs,
  primaryAction,
  secondaryActions,
  titleBadge,
  guide,
  titleAside,
  rail,
  hideHeader,
  className,
  children,
}: DashboardPageProps) {
  return (
    <div className="w-full min-w-0 pb-16 md:pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className={cn("mx-auto w-full max-w-7xl", className)}>
        {!hideHeader && (
          <DashboardHeader
            title={title}
            description={description}
            breadcrumbs={breadcrumbs}
            primaryAction={primaryAction}
            secondaryActions={secondaryActions}
            titleBadge={titleBadge}
            titleAside={
              titleAside ??
              (guide ? <DashboardGuide guide={guide} /> : undefined)
            }
          />
        )}

        {rail ? (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 md:gap-8 items-start">
            <main className="min-w-0 space-y-6 md:space-y-8">{children}</main>
            <aside className="min-w-0 space-y-6">{rail}</aside>
          </div>
        ) : (
          <div className="space-y-6 md:space-y-8">{children}</div>
        )}
      </div>
    </div>
  );
}
